import { ref, toRaw } from 'vue';
import { defineStore } from 'pinia';
import { useBackgroundStore } from '@/stores/useBackgroundStore';

type ColorDefinition = {
  id: string;
  displayName: string;
  hex: `#${string}`;
};

export const colors = [
  {
    id: 'primair-groen',
    displayName: 'Primair groen',
    hex: '#0A0',
  },
  {
    id: 'hardgroen',
    displayName: 'Hardgroen',
    hex: '#AE7',
  },
  {
    id: 'lichtgroen',
    displayName: 'Lichtgroen',
    hex: '#DFD',
  },
  {
    id: 'primair-rood',
    displayName: 'Primair rood',
    hex: '#F00',
  },
  {
    id: 'hardroze',
    displayName: 'Hardroze',
    hex: '#FAC',
  },
  {
    id: 'lichtroze',
    displayName: 'Lichtroze',
    hex: '#FDE',
  },
  {
    id: 'wit',
    displayName: 'Wit',
    hex: '#FFF',
  },
  {
    id: 'zwart',
    displayName: 'Zwart',
    hex: '#000',
  },
] as const satisfies ColorDefinition[];

type Color = (typeof colors)[number];
export type ColorId = Color['id'];

export type ColorScheme = {
  id: string;
  label: string;
  colorIds: [ColorId, ColorId, ColorId];
  colors: [Color['hex'], Color['hex'], Color['hex']];
};

export type ThemeOption = {
  id: string;
  label: string;
};

function getColorById (id: ColorId): Color {
  return colors.find((color) => color.id === id)!;
}

function createColorScheme (colorIds: [ColorId, ColorId, ColorId]): ColorScheme {
  return {
    id: colorIds.join('-'),
    label: colorIds.map((id) => getColorById(id).displayName).join(' — '),
    colorIds,
    colors: colorIds.map((id) => getColorById(id).hex) as [Color['hex'], Color['hex'], Color['hex']],
  };
}

export const useFormStore = defineStore('form', () => {
  const colorOptions = ref(colors.map((color) => ({
    id: color.id,
    label: color.displayName,
    hex: color.hex,
  })));

  const themeOptions = ref<ThemeOption[]>([
    'Samen',
    'Sociaal',
    'Klimaat',
    'Natuur',
    'Groen',
    'Gelijkheid',
    'Vooruitgang',
    'Onderwijs',
    'Zorg',
    'Toegankelijk',
    'Feminisme',
    'Vrijheid',
    'Veiligheid',
    'Democratie',
    'Wonen',
    'Europa',
    'Sport',
    'Cultuur',
    'Gezondheid',
  ].map((label) => ({
    id: label,
    label,
  })));

  const selectedThemeIds = ref<Array<string | null>>([null, null, null]);

  const colorSchemes = ref<ColorScheme[]>([
    createColorScheme(['primair-rood', 'primair-groen', 'hardroze']),
    createColorScheme(['lichtgroen', 'primair-groen', 'hardroze']),
    createColorScheme(['lichtroze', 'hardroze', 'hardgroen']),
  ]);

  const selectedThemeColorIds = ref<Array<ColorId | null>>(
    structuredClone(toRaw(colorSchemes.value[0]!.colorIds)),
  );

  function setSelectedTheme (index: number, themeId: string | null) {
    selectedThemeIds.value[index] = themeId;
  }

  function setSelectedThemeColor (index: number, colorId: ColorId | null) {
    selectedThemeColorIds.value[index] = colorId;
  }

  function getColorHexById (colorId: ColorId | null): Color['hex'] | null {
    if (colorId == null) {
      return null;
    }

    return getColorById(colorId).hex;
  }

  function applyColorScheme (colorSchemeId: string) {
    const selectedScheme = colorSchemes.value.find((scheme) => scheme.id === colorSchemeId);

    if (selectedScheme == null) {
      return;
    }

    selectedThemeColorIds.value = [...selectedScheme.colorIds];
  }

  function moveSelectedTheme (fromIndex: number, toIndex: number) {
    const reorderedThemeIds = [...selectedThemeIds.value];
    const movedThemeId = reorderedThemeIds.splice(fromIndex, 1)[0] ?? null;
    reorderedThemeIds.splice(toIndex, 0, movedThemeId);
    selectedThemeIds.value = reorderedThemeIds;

    const reorderedThemeColorIds = [...selectedThemeColorIds.value];
    const movedThemeColorId = reorderedThemeColorIds.splice(fromIndex, 1)[0] ?? null;
    reorderedThemeColorIds.splice(toIndex, 0, movedThemeColorId);
    selectedThemeColorIds.value = reorderedThemeColorIds;
  }

  function getThemeAndColorSelectionValidationMessage (): string | null {
    const backgroundStore = useBackgroundStore();

    if (!backgroundStore.hasBackgroundImage) {
      return 'Voeg eerst een foto toe voordat je exporteert.';
    }

    if (selectedThemeIds.value.some((themeId) => themeId == null || themeId === '')) {
      return 'Kies eerst alle 3 de thema\'s voordat je exporteert.';
    }

    if (selectedThemeColorIds.value.some((colorId) => colorId == null)) {
      return 'Kies 3 verschillende kleuren voordat je exporteert.';
    }

    const uniqueColorCount = new Set(selectedThemeColorIds.value).size;

    if (uniqueColorCount !== 3) {
      return 'Gebruik 3 verschillende kleuren voordat je exporteert.';
    }

    return null;
  }

  return {
    colorOptions,
    colorSchemes,
    themeOptions,
    selectedThemeIds,
    selectedThemeColorIds,
    getColorHexById,
    getThemeAndColorSelectionValidationMessage,
    applyColorScheme,
    setSelectedTheme,
    setSelectedThemeColor,
    moveSelectedTheme,
  };
});
